// Admin surface for the exam-profile registry. Exams are data, not code: adding
// or editing a profile here writes a Firestore document and a new exam appears
// across the app with no deploy. Top-level fields and the content-area weights
// get real form controls; the more structural parameters (mode mix, cognitive
// distribution, framing) are edited as validated JSON.
//
// Writes go through the upsertExamProfile CALLABLE (lib/functions), not a direct
// Firestore write — the security rules deny direct client writes to examProfiles.
// The callable re-checks the admin claim and validates/normalizes the document
// shape server-side, so a malformed profile can never be persisted. Client-side
// checks here are just for fast feedback.

import { useEffect, useState } from 'react';
import { listExamProfiles } from '../lib/db';
import { upsertExamProfile } from '../lib/functions';

const BLANK_ADVANCED = {
  questionModeMix: { single_best_answer: 1, extended_matching: 0, multiple_response: 0 },
  cognitiveLevelDistribution: { recall: 0.3, application: 0.5, analysis: 0.2 },
  competencyMix: {
    medical_knowledge: 40,
    diagnosis: 25,
    management: 12,
    communication: 2,
    professionalism: 6,
    systems_based_practice: 7,
    practice_based_learning: 8,
  },
  framing: {
    vignetteStyle: 'clinical_vignette',
    optionCount: 5,
    audience: 'medical student',
    tone: 'formal_clinical',
    includeLabValues: true,
  },
};

function slugify(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function profileToDraft(p) {
  const { questionModeMix, cognitiveLevelDistribution, competencyMix, framing } = p;
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    active: p.active !== false,
    stripePriceId: p.stripePriceId ?? '',
    contentAreas: p.contentAreas.map((c) => ({ ...c })),
    advancedJson: JSON.stringify(
      { questionModeMix, cognitiveLevelDistribution, competencyMix, framing },
      null,
      2,
    ),
  };
}

function blankDraft() {
  return {
    id: '',
    name: '',
    description: '',
    active: true,
    stripePriceId: '',
    contentAreas: [{ id: 'general', label: 'General', weight: 1 }],
    advancedJson: JSON.stringify(BLANK_ADVANCED, null, 2),
  };
}

export default function Admin() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null); // null = new
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  async function refresh() {
    setLoading(true);
    const list = await listExamProfiles();
    setProfiles(list.sort((a, b) => a.name.localeCompare(b.name)));
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, []);

  function startNew() {
    setDraft(blankDraft());
    setEditingId(null);
    setErr(null);
    setOk(null);
  }

  function startEdit(p) {
    setDraft(profileToDraft(p));
    setEditingId(p.id);
    setErr(null);
    setOk(null);
  }

  function updateArea(i, patch) {
    if (!draft) return;
    const next = draft.contentAreas.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    setDraft({ ...draft, contentAreas: next });
  }

  function addArea() {
    if (!draft) return;
    setDraft({
      ...draft,
      contentAreas: [...draft.contentAreas, { id: '', label: '', weight: 1 }],
    });
  }

  function removeArea(i) {
    if (!draft) return;
    setDraft({ ...draft, contentAreas: draft.contentAreas.filter((_, idx) => idx !== i) });
  }

  async function save() {
    if (!draft) return;
    setErr(null);
    setOk(null);

    const id = editingId ?? (draft.id.trim() || slugify(draft.name));
    if (!id) {
      setErr('An id (or a name to derive it from) is required.');
      return;
    }
    if (!draft.name.trim()) {
      setErr('Name is required.');
      return;
    }
    const areas = draft.contentAreas
      .map((c) => ({
        id: c.id.trim() || slugify(c.label),
        label: c.label.trim(),
        weight: Number(c.weight) || 0,
      }))
      .filter((c) => c.label && c.id);
    if (areas.length === 0) {
      setErr('At least one content area with a label is required.');
      return;
    }

    let advanced;
    try {
      advanced = JSON.parse(draft.advancedJson);
      if (
        !advanced.questionModeMix ||
        !advanced.cognitiveLevelDistribution ||
        !advanced.framing
      ) {
        throw new Error('missing keys');
      }
    } catch {
      setErr(
        'Advanced JSON is invalid. It must parse and contain questionModeMix, cognitiveLevelDistribution, and framing.',
      );
      return;
    }

    const profile = {
      id,
      name: draft.name.trim(),
      description: draft.description.trim(),
      active: draft.active,
      stripePriceId: draft.stripePriceId.trim() || null,
      contentAreas: areas,
      questionModeMix: advanced.questionModeMix,
      cognitiveLevelDistribution: advanced.cognitiveLevelDistribution,
      competencyMix: advanced.competencyMix,
      framing: advanced.framing,
    };

    setSaving(true);
    try {
      // Server-validated write. The callable throws invalid-argument with a
      // specific message if the shape is off, or permission-denied if not admin.
      await upsertExamProfile(profile);
      setOk(`Saved “${profile.name}”.`);
      setDraft(null);
      setEditingId(null);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Write failed (are you an admin?).');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <span className="eyebrow">Admin</span>
        <h1>Exam profiles</h1>
        <p>Add or edit an exam's blueprint. Changes take effect immediately — no deploy.</p>
      </div>

      {ok && (
        <div className="ok-note" style={{ marginBottom: '1.25rem' }}>
          {ok}
        </div>
      )}

      <div className="card">
        <div className="row between">
          <div className="panel-title" style={{ margin: 0 }}>
            Registry ({profiles.length})
          </div>
          <button className="btn btn-primary btn-sm" onClick={startNew}>
            + New profile
          </button>
        </div>

        {loading ? (
          <div className="muted" style={{ marginTop: '1rem' }}>
            Loading…
          </div>
        ) : profiles.length === 0 ? (
          <div className="empty" style={{ marginTop: '1rem' }}>
            No exam profiles yet. Create the first one.
          </div>
        ) : (
          <table className="data" style={{ marginTop: '1rem' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Content areas</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td className="mono" style={{ fontSize: '0.78rem' }}>
                    {p.id}
                  </td>
                  <td>{p.contentAreas.length}</td>
                  <td>
                    <span
                      className={`badge ${p.active !== false ? 'badge-active' : 'badge-inactive'}`}
                    >
                      {p.active !== false ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => startEdit(p)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {draft && (
        <div className="card">
          <div className="panel-title">
            {editingId ? `Edit: ${editingId}` : 'New exam profile'}
          </div>

          <div className="stack">
            <div className="grid-2">
              <div>
                <label className="eyebrow">Exam ID</label>
                <input
                  className="field"
                  value={draft.id}
                  disabled={editingId !== null}
                  placeholder="auto from name"
                  onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                />
                <div className="muted" style={{ fontSize: '0.76rem' }}>
                  {editingId
                    ? 'IDs are immutable once created.'
                    : 'Leave blank to derive from name.'}
                </div>
              </div>
              <div>
                <label className="eyebrow">Name</label>
                <input
                  className="field"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="eyebrow">Description</label>
              <input
                className="field"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>

            <div>
              <label className="eyebrow">Stripe price id (recurring)</label>
              <input
                className="field mono"
                placeholder="price_… (leave blank if not yet purchasable)"
                value={draft.stripePriceId}
                onChange={(e) => setDraft({ ...draft, stripePriceId: e.target.value })}
                spellCheck={false}
              />
              <div className="muted" style={{ fontSize: '0.76rem' }}>
                The recurring Stripe Price that grants access to this exam. Create it in Stripe, paste
                the id here. Blank = exam exists but isn't on sale.
              </div>
            </div>

            <label className="row" style={{ gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
              />
              <span>Active (visible to subscribers)</span>
            </label>

            <div>
              <div className="row between">
                <label className="eyebrow">Content areas &amp; weights</label>
                <button className="btn btn-ghost btn-sm" onClick={addArea}>
                  + Add area
                </button>
              </div>
              <div className="stack" style={{ marginTop: '0.5rem' }}>
                {draft.contentAreas.map((c, i) => (
                  <div className="row" key={i} style={{ gap: '0.5rem' }}>
                    <input
                      className="field"
                      style={{ flex: 2 }}
                      placeholder="Label (e.g. Cardiovascular)"
                      value={c.label}
                      onChange={(e) => updateArea(i, { label: e.target.value })}
                    />
                    <input
                      className="field"
                      style={{ flex: 1 }}
                      placeholder="id"
                      value={c.id}
                      onChange={(e) => updateArea(i, { id: e.target.value })}
                    />
                    <input
                      className="field"
                      style={{ width: 90 }}
                      type="number"
                      step="0.01"
                      placeholder="weight"
                      value={c.weight}
                      onChange={(e) => updateArea(i, { weight: Number(e.target.value) })}
                    />
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => removeArea(i)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className="muted" style={{ fontSize: '0.76rem', marginTop: '0.4rem' }}>
                Weights are relative; they're normalized at retrieval time.
              </div>
            </div>

            <div>
              <label className="eyebrow">Advanced (mode mix · cognitive levels · framing)</label>
              <textarea
                className="field mono"
                style={{ minHeight: 200, fontSize: '0.8rem' }}
                value={draft.advancedJson}
                onChange={(e) => setDraft({ ...draft, advancedJson: e.target.value })}
                spellCheck={false}
              />
            </div>

            {err && <div className="error-note">{err}</div>}

            <div className="row">
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save profile'}
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setDraft(null);
                  setEditingId(null);
                  setErr(null);
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
